import React, { useState, useEffect } from 'react'
import Modal, { ModalProps } from 'components/modal'
import Button from 'components/button'
import Select from 'components/select'
import api from 'helpers/api'
import { IProcessSectionObject } from 'types'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { useDataContext } from 'components/document/contexts/data'

const RestoreProcessSection: React.FC<ModalProps> = ({ id, open, setOpen, onClose }) => {
  const { process, setProcess } = useDataContext()
  const [selectedProcessSection, setSelectedProcessSection] = useState<string>('')
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const [deletedSections, setDeletedSections] = useState<Array<IProcessSectionObject>>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (process && process.publicId !== '') {
      setLoading(true)
      api
        .getDeletedProcessSections(process.publicId)
        .then((response) => {
          setDeletedSections(response.data)
          setLoading(false)
        })
        .catch((error) => {
          displayErrorMessage(error)
          setLoading(false)
        })
    }
  }, [process && process.publicId])

  const updateProcessSection = (
    sectionId: string,
    children: IProcessSectionObject[],
    sectionUpdated: IProcessSectionObject
  ): IProcessSectionObject[] => {
    children.forEach((child) => {
      if (child.publicId === sectionUpdated?.parentId) {
        child.children.push(sectionUpdated)
        return children
      } else {
        return updateProcessSection(sectionId, child.children, sectionUpdated)
      }
    })
    return children
  }

  const restoreProcessSection = () => {
    if (process && selectedProcessSection && selectedProcessSection !== '') {
      api
        .restoreProcessSection(project.publicId, process.publicId, selectedProcessSection)
        .then((response) => {
          setOpen(false)
          setProcess({
            ...process,
            children: updateProcessSection(response.data.publicId, process.children, response.data)
          })

          setSnackbarMessage({ status: 'success' })
        })
        .catch((error) => {
          displayErrorMessage(error)
          setOpen(false)
        })
    }
  }

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Restore Document Section" onClose={onClose}>
      Please select which archived document section you wish to restore:
      <br />
      <br />
      <Select
        options={[
          { value: '', label: 'None Selected' },
          ...(deletedSections
            ? deletedSections
                .filter((section) => section.deletedAt)
                .map((section) => ({
                  value: section.publicId,
                  label: section.name + ' (Deleted On ' + section.deletedAt!.split('T')[0] + ')'
                }))
            : [])
        ]}
        loading={loading}
        optionsSelected={selectedProcessSection ? [selectedProcessSection] : ['']}
        onOptionClick={(option) => setSelectedProcessSection(option)}
      />
      <div className="flex w-full">
        <Button
          className="mt-10 ml-auto"
          onClick={() => restoreProcessSection()}
          data-cy="restore-process-section-button"
          isLoading={loading}
          disabled={!(selectedProcessSection && selectedProcessSection !== '')}
        >
          Restore Document Section
        </Button>
      </div>
    </Modal>
  )
}

export default RestoreProcessSection
